/**
 * One Google tag (gtag.js) for GA4 + Google Ads.
 *
 * IDs are runtime config only (Fly secrets on Live): GA_MEASUREMENT_ID and
 * GOOGLE_ADS_ID, plus optional GOOGLE_ADS_SIGNUP_LABEL for an Ads conversion.
 * The server injects them as window.__GA_MEASUREMENT_ID__ / __GOOGLE_ADS_ID__ /
 * __GOOGLE_ADS_SIGNUP_LABEL__ (server/api-util/googleTagBoot.js). When none is
 * set (TEST), no Google script is loaded at all.
 */

const ADS_ID_RE = /^AW-\d{6,15}$/;
const ADS_LABEL_RE = /^[A-Za-z0-9_-]{4,64}$/;
// Query params that can carry tokens or emails (verify-email, reset-password, etc).
const SENSITIVE_PARAM_RE = /^(t|e|token|code|key|email|password|access_token|id_token|auth)$|token|email|secret/i;

const clean = value => (typeof value === 'string' ? value.trim() : '');

export const normalizeGaId = value => {
  const id = clean(value);
  return id.indexOf('G-') === 0 ? id : null;
};

export const normalizeAdsId = value => {
  const id = clean(value);
  return ADS_ID_RE.test(id) ? id : null;
};

export const normalizeAdsLabel = value => {
  // Also accept the full "AW-123456789/label" send_to string from Google Ads.
  const label = clean(value).replace(/^AW-\d{6,15}\//, '');
  return ADS_LABEL_RE.test(label) ? label : null;
};

const fromWindow = key => (typeof window !== 'undefined' ? window[key] : undefined);

/** @returns {string|null} Google Ads tag id (AW-…) or null. */
export const envGoogleAdsId = () =>
  normalizeAdsId(process.env.REACT_APP_GOOGLE_ADS_ID) ||
  normalizeAdsId(fromWindow('__GOOGLE_ADS_ID__'));

/** @returns {string|null} Ads conversion label for sign-ups, or null. */
export const envGoogleAdsSignupLabel = () =>
  normalizeAdsLabel(process.env.REACT_APP_GOOGLE_ADS_SIGNUP_LABEL) ||
  normalizeAdsLabel(fromWindow('__GOOGLE_ADS_SIGNUP_LABEL__'));

/** @returns {string|null} "AW-123/label" when both id and label are set. */
export const googleAdsSignupSendTo = () => {
  const adsId = envGoogleAdsId();
  const label = envGoogleAdsSignupLabel();
  return adsId && label ? `${adsId}/${label}` : null;
};

/**
 * Which IDs to configure and which single ID the gtag.js loader uses.
 *
 * @param {string|null} gaIdCandidate
 * @param {string|null} [adsIdCandidate] defaults to envGoogleAdsId()
 */
export const googleTagIds = (gaIdCandidate, adsIdCandidate) => {
  const gaId = normalizeGaId(gaIdCandidate);
  const adsId =
    adsIdCandidate === undefined ? envGoogleAdsId() : normalizeAdsId(adsIdCandidate);
  return { gaId, adsId, loaderId: gaId || adsId };
};

/**
 * Drop query params that could carry tokens or emails before Google sees the URL.
 *
 * @param {string} href
 * @returns {string}
 */
export const safePageLocation = href => {
  if (typeof href !== 'string' || !href) {
    return href;
  }
  try {
    const url = new URL(href);
    Array.from(url.searchParams.keys()).forEach(key => {
      if (SENSITIVE_PARAM_RE.test(key)) {
        url.searchParams.delete(key);
      }
    });
    url.hash = '';
    return url.toString();
  } catch (e) {
    return href.split('?')[0].split('#')[0];
  }
};

/**
 * Set up window.gtag once and send one `config` per destination.
 * Safe to call on every render: each ID is configured only once per page load.
 *
 * @returns {boolean} true when a Google tag is configured
 */
export const configureGoogleTag = (win, { gaId, adsId } = {}) => {
  if (!win || (!gaId && !adsId)) {
    return false;
  }
  win.dataLayer = win.dataLayer || [];
  if (typeof win.gtag !== 'function') {
    win.gtag = function gtag() {
      // gtag.js needs the Arguments object, not an array.
      win.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
    };
  }
  const done = win.__slGoogleTagConfigured || {};
  if (!done.js) {
    win.gtag('js', new Date());
    const pageLocation = safePageLocation(win.location && win.location.href);
    if (pageLocation) {
      win.gtag('set', { page_location: pageLocation });
    }
    done.js = true;
  }
  if (gaId && !done[gaId]) {
    win.gtag('config', gaId, {
      cookie_flags: 'SameSite=None;Secure',
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    done[gaId] = true;
  }
  if (adsId && !done[adsId]) {
    win.gtag('config', adsId);
    done[adsId] = true;
  }
  win.__slGoogleTagConfigured = done;
  return true;
};
